import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { useSite } from "../../../../../../../store/useSite";
import { useBase } from "../../../../../../../store/useBase";
import { useEffect, useRef, useState } from "react";
import { logoutSiteUser } from "../../../../../../../services/auth";
import { User, Menu, X, ChevronDown, Store, Ticket as TicketIcon } from "lucide-react";
import ExhibitorProfileSettings from "../../../../../../siteExhibitors/ExhibitorProfileSettings";
import ExhibitorOnboarding from "../../../../../../siteExhibitors/ExhibitorOnboarding";
import { useEventTiming } from "../../../../../../../hooks/useEventTiming";
import SidebarPwaInstall from "../../../../../../pwa/SidebarPwaInstall";

export const Header1 = ({ dark, hiddenMenus }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const {
    siteData,
    setEventData,
    eventData,
    mode,
    siteUser,
    setProfileSettingPopup,
    setSiteUser,
    siteHideData,
    setMode,
    speakerData,
    sponsorData,
    isExhibitor,
    isExhibitorSpace,
    setProfileModalMode,
    profileSettingPopup,
    profileModalMode,
  } = useSite();
  const { eventId, siteSlug } = useBase();

  const isEditing = mode === "edit" || mode === "tickets" || mode === "ticket";
  const eventTiming = useEventTiming(eventData, false);
  const eventEnded = !isEditing && eventTiming.isEnded;

  const handleRegistrationClick = (e) => {
    if (isEditing) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setMode("tickets");
    }
  };
  const fileInputRef = useRef(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [imgElement, setImgElement] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [baseScale, setBaseScale] = useState(1);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [imgStart, setImgStart] = useState({ x: 0, y: 0 });
  const cropBoxRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedMobileDropdown, setExpandedMobileDropdown] = useState(null);

  const [hideForSlug, setHideForSlug] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      if (pathName !== getPath("")) {
        setActiveTab("");
        return;
      }

      const sections = ["speaker", "venue", "sponsor"];
      const scrollPosition = window.scrollY;

      if (scrollPosition < 200) {
        setActiveTab("home");
        return;
      }

      let current = "";
      for (const section of sections) {
        const el = document.getElementById(`${section}Section`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            current = section;
            break;
          }
        }
      }
      setActiveTab(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathName, siteData]);

  useEffect(() => {
    setMenuOpen(false);
    const id = `${mode}Section`;
    if (mode === "speaker" || mode === "venue" || mode === "sponsor") {
      const el = document.getElementById(id);
      if (el) {
        const headerOffset = 40;
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, [mode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
      if (!event.target.closest(".profile-dropdown-container")) {
        setProfileOpen(false);
        setMobileProfileOpen(false);
      }
    };

    if (openDropdown !== null || profileOpen || mobileProfileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openDropdown, profileOpen, mobileProfileOpen]);

  const handleDeleteLogo = () => {
    setEventData((prev) => ({
      ...prev,
      logo: null,
    }));
    setPreviewLogo(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

  const onWheelZoom = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    const factor = delta > 0 ? 1.05 : 0.95;
    let next = clamp(scale * factor, 0.5, 4);
    setScale(next);
  };

  const onPointerDown = (e) => {
    setPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY });
    setImgStart({ x: offset.x, y: offset.y });
  };

  const onPointerMove = (e) => {
    if (!panning) return;
    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;
    setOffset({ x: imgStart.x + dx, y: imgStart.y + dy });
  };

  const onPointerUp = () => {
    setPanning(false);
  };

  const confirmCrop = async () => {
    if (!imgElement) return;
    const canvasW = 600;
    const canvasH = 280;
    const canvas = document.createElement("canvas");
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvasW, canvasH);
    ctx.save();

    ctx.rect(0, 0, canvasW, canvasH);
    ctx.clip();

    const boxW = 360;
    const boxH = 360 * (280 / 600);
    const finalScale = baseScale * scale;
    const drawW = imgSize.w * finalScale;
    const drawH = imgSize.h * finalScale;
    const canvasScale = canvasW / boxW;

    const dx = (boxW / 2 - drawW / 2 + offset.x) * canvasScale;
    const dy = (boxH / 2 - drawH / 2 + offset.y) * canvasScale;

    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imgElement, dx, dy, drawW * canvasScale, drawH * canvasScale);
    ctx.restore();

    await new Promise((r) => setTimeout(r, 0));
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "logo.png", { type: "image/png" });
      const previewUrl = URL.createObjectURL(blob);

      setPreviewLogo(previewUrl);
      setEventData((prev) => ({
        ...prev,
        logo: previewUrl,
        logoFile: file,
      }));

      setCropOpen(false);
      if (rawImage?.src) URL.revokeObjectURL(rawImage.src);
      setRawImage(null);
      setImgElement(null);
    }, "image/png");
  };

  const cancelCrop = () => {
    setCropOpen(false);
    if (rawImage?.src) {
      URL.revokeObjectURL(rawImage.src);
    }
    setRawImage(null);
    setImgElement(null);
    setOffset({ x: 0, y: 0 });
    setScale(1);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("File size exceeds 5MB.");
      e.target.value = "";
      return;
    }

    const src = URL.createObjectURL(file);
    setRawImage({ file, src });

    const img = new Image();
    img.onload = () => {
      setImgElement(img);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      setImgSize({ w, h });

      const boxW = 360;
      const boxH = 360 * (280 / 600);

      const contain = Math.min(boxW / w, boxH / h);
      setBaseScale(contain);
      setScale(1);
      setOffset({ x: 0, y: 0 });

      setCropOpen(true);
    };
    img.src = src;
    e.target.value = "";
  };

  const handleProfile = () => {
    setProfileOpen(!profileOpen);
    setOpenDropdown(null);
  };

  const handleMobileProfile = () => {
    setMobileProfileOpen(!mobileProfileOpen);
    setExpandedMobileDropdown(null);
  };

  const handleDashboard = () => {
    navigate(`/${siteSlug}/your-tickets`);
    setProfileOpen(false);
    setMobileProfileOpen(false);
  };

  const handleProfileSection = () => {
    if (isExhibitor) {
      setProfileModalMode("edit"); // Force manual edit mode
      setProfileSettingPopup(true);
    } else {
      navigate(`/${siteSlug}/dashboard`);
    }
    setProfileOpen(false);
    setMobileProfileOpen(false);
  };

  const handleExhibitorProfileSection = () => {
    if (isExhibitor) {
      setProfileModalMode("edit"); // Force manual edit mode
      setProfileSettingPopup(true);
    }
    setProfileOpen(false);
    setMobileProfileOpen(false);
  };


  const handleExhibitorSpace = () => {
    navigate(`/${siteSlug}/exhibitor/space/overview`);
    setProfileOpen(false);
    setMobileProfileOpen(false);
  };

  const handleLogout = async () => {
    setProfileOpen(false);
    setMobileProfileOpen(false);
    setSiteUser(null);
    const res = await logoutSiteUser(eventId);
    if (res.status === 200) {
      navigate(`/${siteSlug}`);
    }
  };

  useEffect(() => {
    if (!siteSlug) return;
    const ex = ["aikyam-event", "world-health-and-wellness-fest-2025"];

    if (ex.includes(siteSlug)) {
      setHideForSlug(true);
    }
  }, [siteSlug]);

  useEffect(() => {
    if (siteUser?.is_first_login === 1 && isExhibitor && profileModalMode === "auto") {
      setProfileSettingPopup(true);
    }
  }, [siteUser, isExhibitor, profileModalMode, setProfileSettingPopup]);

  const getPath = (subPath) => {
    const slug = siteSlug || eventData?.slug;
    if (slug === "registration-will-be-open-soon") {
      return subPath ? `/${subPath}` : "/";
    }
    return subPath ? `/${slug}/${subPath}` : `/${slug}`;
  };

  const isExhibitorSpaceOverview = pathName === getPath("exhibitor/space/overview");

  return (
    <div
      className={`${dark ? "bg-[#171C26]" : "bg-[#ffffff]"} ${menuOpen || mobileProfileOpen || profileOpen ? "z-[90]" : "z-[80]"} h-[65px] justify-center md:h-auto border-b ${mode !== "edit" ? "sticky top-0 z-[80]" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-30 h-[65px]">
        <div className="flex items-center justify-center md:justify-between h-full md:h-16">
          {!hiddenMenus && (
            <div className={`md:hidden ${hideForSlug && "hidden"}`}>
              <button
                className={`text-3xl ${dark ? "text-[#ffffff]" : "text-[#131517]"} focus:outline-none flex justify-center items-center`}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? (
                  <X
                    className={`w-6 h-6 ${dark ? "text-white" : "text-black"}`}
                  />
                ) : (
                  <Menu
                    className={`w-6 h-6 ${dark ? "text-white" : "text-black"}`}
                  />
                )}
              </button>
            </div>
          )}

          <div className="flex justify-center ml-2 mr-auto md:mx-0">
            {(previewLogo || eventData?.logo) && mode !== "edit" ? (
              <div>
                <img
                  src={previewLogo || eventData.logo}
                  alt="Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
            ) : !previewLogo && !eventData?.logo && mode === "edit" ? (
              <>
                <button
                  onClick={handleUploadClick}
                  className="border-2 border-dashed border-blue-500 text-blue-500 px-4 py-1 rounded text-lg font-light"
                >
                  Upload Logo
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </>
            ) : (previewLogo || eventData?.logo) && mode === "edit" ? (
              <div className="relative inline-block">
                <img
                  src={previewLogo || eventData.logo}
                  alt="Logo"
                  className="h-12 w-auto object-contain"
                />
                <span
                  onClick={handleDeleteLogo}
                  className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-md cursor-pointer"
                >
                  ✕
                </span>
              </div>
            ) : (
              <p
                className={`${dark ? "text-white" : "text-black"} font-semibold text-xl`}
              >
                {eventData?.event_name?.split(" ")[0]}
              </p>
            )}
          </div>

          <div className={`md:hidden ${hideForSlug && "invisible"}`}>
            {!siteUser ? (
              !isExhibitorSpaceOverview && (
                <Link
                  to={getPath("signin")}
                  className={`text-sm font-semibold ${dark ? "text-[#ffffff] hover:text-[#EAEFFF]" : "text-[#000000] hover:text-[#3A3A3A]"} transition-colors`}
                >
                  SIGN IN
                </Link>
              )
            ) : (
              <div className="relative z-50 profile-dropdown-container">
                <div
                  onClick={handleMobileProfile}
                  className="w-10 h-10 rounded-full bg-gray-400 cursor-pointer flex items-center justify-center text-white overflow-hidden"
                >
                  {siteUser?.profile ||
                      (isExhibitor && siteUser?.booth_member?.profile_image) ? (
                    <img
                      src={
                        isExhibitor
                          ? siteUser?.booth_member?.profile_image
                          : siteUser?.profile
                      }
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-6 h-6" />
                  )}
                </div>
                {mobileProfileOpen && (
                  <div className="absolute right-0 mt-4 w-52 bg-white rounded-md shadow-lg z-[100] text-sm border border-gray-100">
                    {isExhibitor ? (
                      <button
                        onClick={handleExhibitorProfileSection}
                        className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                      >
                        <User
                          size={16}
                          className="mr-3 text-gray-400 group-hover:text-black"
                        />
                        Profile
                      </button>
                    ) : (
                      <button
                        onClick={handleProfileSection}
                        className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                      >
                        <User
                          size={16}
                          className="mr-3 text-gray-400 group-hover:text-black"
                        />
                        Profile
                      </button>
                    )}
                 {isExhibitor&&   <button
                      onClick={handleDashboard}
                      className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                    >
                      <TicketIcon
                        size={16}
                        className="mr-3 text-gray-400 group-hover:text-black"
                      />
                      Your Tickets
                    </button>}
                    {isExhibitorSpace && (
                      <button
                        onClick={handleExhibitorSpace}
                        className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                      >
                        <Store
                          size={16}
                          className="mr-3 text-gray-400 group-hover:text-black"
                        />
                        Exhibitor Space
                      </button>
                    )}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-3 text-black hover:bg-gray-100 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="hidden md:flex items-center gap-6">
            {!hiddenMenus && (
              <>
                {isExhibitorSpaceOverview ? (
                  <>
                    <Link
                      to={getPath("")}
                      className={`text-sm font-semibold transition-colors ${dark
                        ? "text-[#ffffff] hover:text-[#EAEFFF]"
                        : "text-[#000000] hover:text-[#3A3A3A]"
                        } px-2 py-1 rounded`}
                    >
                      HOME
                    </Link>
                    <Link
                      to={getPath("exhibitor/space/overview")}
                      className={`text-sm font-semibold transition-colors ${dark
                        ? "bg-white text-black font-semibold"
                        : "bg-black text-white font-semibold"
                        } px-2 py-1 rounded`}
                    >
                      EXHIBITOR SPACE
                    </Link>
                  </>
                ) : (
                  siteData?.[0]?.menu?.map((item, index) => {
                    if (
                      item.title.toLowerCase() === "sign in" ||
                      item.title.toLowerCase() === "login"
                    )
                      return null;

                    if (item.title === "Home") {
                      const path = getPath("");
                      const isActive =
                        pathName === path &&
                        (activeTab === "home" || activeTab === "");
                      return (
                        <Link
                          key={index}
                          to={path}
                          onClick={(e) => {
                            if (isEditing) {
                              e.preventDefault();
                              setMode("edit");
                            }
                            setMenuOpen(false);
                          }}
                          className={`text-sm font-semibold transition-colors ${isActive
                            ? dark
                              ? "bg-white text-black font-semibold"
                              : "bg-black text-white font-semibold"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>
                      );
                    }

                    if (item.title === "Agenda") {
                      if (!siteHideData?.agenda) return null;
                      const path = getPath("agenda");
                      return (
                        <Link
                          key={index}
                          to={path}
                          onClick={(e) => {
                            if (isEditing) {
                              e.preventDefault();
                              setMode("agenda");
                            }
                            setMenuOpen(false);
                          }}
                          className={`text-sm font-semibold transition-colors ${pathName === path
                            ? dark
                              ? "bg-white text-black font-semibold"
                              : "bg-black text-white font-semibold"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>
                      );
                    }

                    if (item.title === "Terms & Conditions") {
                      const termsPath = getPath("terms");
                      const aboutPath = getPath("about");
                      const contactPath = getPath("contact");
                      return [
                        <Link
                          key={index}
                          to={termsPath}
                          onClick={(e) => {
                            if (isEditing) {
                              e.preventDefault();
                              setMode("terms");
                            }
                            setMenuOpen(false);
                          }}
                          className={`text-sm font-semibold transition-colors ${pathName === termsPath
                            ? dark
                              ? "bg-white text-black font-semibold"
                              : "bg-black text-white font-semibold"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>,
                        eventData?.about && eventData.about.trim() !== "" && (
                          <Link
                            key={`${index}-about`}
                            to={aboutPath}
                            onClick={(e) => {
                              if (isEditing) {
                                e.preventDefault();
                                setMode("about");
                              }
                              setMenuOpen(false);
                            }}
                            className={`text-sm font-semibold transition-colors ${pathName === aboutPath
                              ? dark
                                ? "bg-white text-black font-semibold"
                                : "bg-black text-white font-semibold"
                              : dark
                                ? "text-[#ffffff] hover:text-[#EAEFFF]"
                                : "text-[#000000] hover:text-[#3A3A3A]"
                              } px-2 py-1 rounded`}
                          >
                            ABOUT US
                          </Link>
                        ),
                        eventData?.contact && eventData.contact.trim() !== "" && (
                          <Link
                            key={`${index}-contact`}
                            to={contactPath}
                            onClick={(e) => {
                              if (isEditing) {
                                e.preventDefault();
                                setMode("contact");
                              }
                              setMenuOpen(false);
                            }}
                            className={`text-sm font-semibold transition-colors ${pathName === contactPath
                              ? dark
                                ? "bg-white text-black font-semibold"
                                : "bg-black text-white font-semibold"
                              : dark
                                ? "text-[#ffffff] hover:text-[#EAEFFF]"
                                : "text-[#000000] hover:text-[#3A3A3A]"
                              } px-2 py-1 rounded`}
                          >
                            CONTACT
                          </Link>
                        ),
                      ];
                    }

                    if (item.title === "Tickets") {
                      if (!siteHideData?.ticket || eventEnded) return null;
                      const path = getPath("tickets");
                      return isEditing ? (
                        <div
                          key={index}
                          onClick={handleRegistrationClick}
                          className={`text-sm font-semibold transition-colors ${pathName.startsWith(path)
                            ? dark
                              ? "bg-white text-black font-semibold"
                              : "bg-black text-white font-semibold"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded cursor-pointer`}
                        >
                          {item.title.toUpperCase()}
                        </div>
                      ) : (
                        <Link
                          key={index}
                          to={path}
                          className={`text-sm font-semibold transition-colors ${pathName.startsWith(path)
                            ? dark
                              ? "bg-white text-black font-semibold"
                              : "bg-black text-white font-semibold"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>
                      );
                    }

                    if (item.title === "Speaker") {
                      if (!speakerData || speakerData.length === 0) return null;
                      const path = getPath("speaker");
                      const isActive = pathName === path && activeTab === "speaker";
                      return (
                        <Link
                          key={index}
                          to={path}
                          onClick={(e) => {
                            if (isEditing) {
                              e.preventDefault();
                              setMode("speaker");
                            } else {
                              setMenuOpen(false);
                              setMode('speaker');
                            }
                          }}
                          className={`text-sm font-semibold transition-colors ${isActive
                            ? dark
                              ? "bg-white text-black font-semibold hover:text-black"
                              : "bg-black text-white font-semibold hover:text-white"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>
                      );
                    }

                    if (item.title === "Sponsor") {
                      if (!sponsorData || sponsorData.length === 0) return null;
                      const path = getPath("sponsor");
                      const isActive = pathName === path && activeTab === "sponsor";
                      return (
                        <Link
                          key={index}
                          to={path}
                          onClick={(e) => {
                            if (isEditing) {
                              e.preventDefault();
                              setMode("sponsor");
                            } else {
                              setMenuOpen(false);
                              setMode('sponsor');
                            }
                          }}
                          className={`text-sm font-semibold transition-colors ${isActive
                            ? dark
                              ? "bg-white text-black font-semibold hover:text-black"
                              : "bg-black text-white font-semibold hover:text-white"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>
                      );
                    }

                    if (item.title === "Venue") {
                      const path = getPath("venue");
                      const isActive = pathName === path && activeTab === "venue";
                      return (
                        <Link
                          key={index}
                          to={path}
                          onClick={(e) => {
                            if (isEditing) {
                              e.preventDefault();
                              setMode("venue");
                            } else {
                              setMenuOpen(false);
                              setMode('venue');
                            }
                          }}
                          className={`text-sm font-semibold transition-colors ${isActive
                            ? dark
                              ? "bg-white text-black font-semibold hover:text-black"
                              : "bg-black text-white font-semibold hover:text-white"
                            : dark
                              ? "text-[#ffffff] hover:text-[#EAEFFF]"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                            } px-2 py-1 rounded`}
                        >
                          {item.title.toUpperCase()}
                        </Link>
                      );
                    }

                    // Handle dropdown menu items
                    if (item.hasDropdown && item.dropdownItems) {
                      const isDropdownOpen = openDropdown === index;
                      const isActive = item.dropdownItems.some((dropdownItem) => {
                        let resolvedPath = dropdownItem.path
                          .replace(":slug", siteSlug || eventData?.slug || "")
                          .replace(":eventId", eventId || "");

                        // Extract pathname from resolved path (remove query params for comparison)
                        const pathOnly = resolvedPath.split("?")[0];
                        return (
                          pathName === pathOnly ||
                          pathName === resolvedPath ||
                          pathName.includes(pathOnly)
                        );
                      });

                      return (
                        <div key={index} className="relative dropdown-container">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(isDropdownOpen ? null : index);
                            }}
                            className={`text-sm font-semibold transition-colors flex items-center gap-1 ${isActive
                              ? dark
                                ? "bg-white text-black font-semibold"
                                : "bg-black text-white font-semibold"
                              : dark
                                ? "text-[#ffffff] hover:text-[#EAEFFF]"
                                : "text-[#000000] hover:text-[#3A3A3A]"
                              } px-2 py-1 rounded`}
                          >
                            {item.title.toUpperCase()}
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""
                                }`}
                            />
                          </button>
                          {isDropdownOpen && (
                            <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg z-[100] border border-gray-200">
                              {item.dropdownItems.map((dropdownItem, dropIndex) => {
                                const resolvedPath = dropdownItem.path
                                  .replace(":slug", siteSlug || eventData?.slug || "")
                                  .replace(":eventId", eventId || "");

                                // Extract pathname for active state check
                                const pathOnly = resolvedPath.split("?")[0];
                                const currentPathOnly = pathName.split("?")[0];
                                const isDropdownItemActive =
                                  currentPathOnly === pathOnly ||
                                  pathName === resolvedPath ||
                                  pathName.includes(pathOnly);

                                return (
                                  <Link
                                    key={dropIndex}
                                    to={resolvedPath}
                                    onClick={(e) => {
                                      if (isEditing) {
                                        e.preventDefault();
                                      }
                                      setOpenDropdown(null);
                                    }}
                                    className={`block px-4 py-2 text-sm transition-colors ${isDropdownItemActive
                                      ? "bg-gray-100 text-black font-semibold"
                                      : "text-gray-700 hover:bg-gray-50"
                                      } ${dropIndex === 0 ? "rounded-t-md" : ""} ${dropIndex === item.dropdownItems.length - 1
                                        ? "rounded-b-md"
                                        : "border-b border-gray-100"
                                      }`}
                                  >
                                    {dropdownItem.title}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={index}
                        to={
                          item.path && item.path.startsWith("/")
                            ? item.path
                            : getPath(item.path || "")
                        }
                        onClick={(e) => {
                          if (isEditing) {
                            e.preventDefault();
                          }
                        }}
                        className={`text-sm font-semibold ${dark ? "text-[#ffffff] hover:text-[#EAEFFF]" : "text-[#000000] hover:text-blue-700"} transition-colors`}
                      >
                        {item.title.toUpperCase()}
                      </Link>
                    );
                  })
                )}


                {!isExhibitorSpaceOverview && (
                  <div className="relative dropdown-container">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProfileOpen(false);
                        setOpenDropdown(
                          openDropdown === "exhibitors" ? null : "exhibitors",
                        );
                      }}
                      className={`text-sm font-semibold transition-colors flex items-center gap-1 ${openDropdown === "exhibitors" ||
                        pathName === getPath("exhibitors") ||
                        pathName === getPath("exhibitors/category")
                        ? dark
                          ? "bg-white text-black font-semibold"
                          : "bg-black text-white font-semibold"
                        : dark
                          ? "text-[#ffffff] hover:text-[#EAEFFF]"
                          : "text-[#000000] hover:text-[#3A3A3A]"
                        } px-2 py-1 rounded`}
                    >
                      EXHIBITORS
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${openDropdown === "exhibitors" ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                    {openDropdown === "exhibitors" && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg z-[100] border border-gray-200">
                        <Link
                          to={getPath("exhibitors")}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-4 py-2.5 text-sm rounded-t-md border-b border-gray-100 ${pathName === getPath("exhibitors")
                            ? "text-black font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          Exhibitors Listing
                        </Link>
                        <Link
                          to={getPath("/exhibitors/category")}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-4 py-2.5 text-sm rounded-b-md ${pathName === getPath("exhibitors/category")
                            ? "text-black font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          Apply To Exhibit
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}


            {!siteUser ? (
              !isExhibitorSpaceOverview && (
                <Link
                  to={getPath("signin")}
                  className={`text-sm font-semibold transition-colors ${dark
                    ? "text-[#ffffff] hover:text-[#EAEFFF]"
                    : "text-[#000000] hover:text-[#3A3A3A]"
                    }`}
                >
                  SIGN IN
                </Link>
              )
            ) : (
              <div className="relative z-50 profile-dropdown-container">
                <div
                  onClick={handleProfile}
                  className="w-10 h-10 rounded-full bg-gray-400 cursor-pointer flex items-center justify-center text-white overflow-hidden"
                >
                  {siteUser?.profile ||
                      (isExhibitor && siteUser?.booth_member?.profile_image) ? (
                    <img
                      src={
                        isExhibitor
                          ? siteUser?.booth_member?.profile_image
                          : siteUser?.profile
                      }
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-6 h-6" />
                  )}
                </div>
                {profileOpen && (
                  <div className="absolute right-0 mt-4 w-52 bg-white rounded-md shadow-lg z-[100] text-sm border border-gray-100 translate-y-1">
                    {isExhibitor ? (
                      <button
                        onClick={handleExhibitorProfileSection}
                        className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                      >
                        Profile
                      </button>
                    ) : (
                      <button
                        onClick={handleProfileSection}
                        className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                      >
                        Profile
                      </button>
                    )}
                {isExhibitor&&    <button
                      onClick={handleDashboard}
                      className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                    >
                      Your Tickets
                    </button>}

                    {isExhibitorSpace && isExhibitor && (
                      <button
                        onClick={handleExhibitorSpace}
                        className="flex items-center w-full text-left px-4 py-3 text-black hover:bg-gray-100 border-b border-gray-50 group transition-colors"
                      >
                        Exhibitor Space
                      </button>
                    )}

                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-3 text-black hover:bg-gray-100 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {menuOpen && (
          <div
            className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-sm top-[65px] md:hidden transition-transform duration-300 transform ${menuOpen ? "translate-x-0" : "-translate-x-full"
              }`}
          >
            <div className="w-[80%] h-full bg-white flex flex-col items-start justify-start overflow-y-auto">
              {isExhibitorSpaceOverview ? (
                <>
                  <Link
                    to={getPath("")}
                    className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4`}
                  >
                    Home
                  </Link>
                  <Link
                    to={getPath("exhibitor/space/overview")}
                    className={`text-base border-b w-full bg-black text-white font-semibold transition-colors pl-6 py-4`}
                  >
                    Exhibitor Space
                  </Link>
                </>
              ) : (
                <>
                  {siteData?.[0]?.menu
                    ?.filter(
                      (item) =>
                        item.title.toLowerCase() !== "sign in" &&
                        item.title.toLowerCase() !== "login",
                    )
                    .map((item, index) => {
                      if (item.title === "Home") {
                        const path = getPath("");
                        const isActive =
                          pathName === path &&
                          (activeTab === "home" || activeTab === "");
                        return (
                          <Link
                            key={index}
                            to={path}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${isActive
                              ? "bg-black text-white font-semibold"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>
                        );
                      }

                      if (item.title === "Agenda") {
                        if (!siteHideData?.agenda) return null;
                        const path = getPath("agenda");
                        return (
                          <Link
                            key={index}
                            to={path}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${pathName === path
                              ? "bg-black text-white font-semibold"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>
                        );
                      }

                      if (item.title === "Terms & Conditions") {
                        const termsPath = getPath("terms");
                        const aboutPath = getPath("about");
                        const contactPath = getPath("contact");
                        return [
                          <Link
                            key={index}
                            to={termsPath}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${pathName === termsPath
                              ? "bg-black text-white font-semibold"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>,
                          eventData?.about && eventData.about.trim() !== "" && (
                            <Link
                              key={`${index}-about`}
                              to={aboutPath}
                              className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${pathName === aboutPath
                                ? "bg-black text-white font-semibold"
                                : "text-[#000000] hover:text-[#3A3A3A]"
                                }`}
                            >
                              About Us
                            </Link>
                          ),
                          eventData?.contact && eventData.contact.trim() !== "" && (
                            <Link
                              key={`${index}-contact`}
                              to={contactPath}
                              className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${pathName === contactPath
                                ? "bg-black text-white font-semibold"
                                : "text-[#000000] hover:text-[#3A3A3A]"
                                }`}
                            >
                              Contact
                            </Link>
                          ),
                        ];
                      }

                      if (item.title === "Speaker") {
                        if (!speakerData || speakerData.length === 0) return null;
                        const path = getPath("speaker");
                        const isActive =
                          pathName === path && activeTab === "speaker";
                        return (
                          <Link
                            key={index}
                            to={path}
                            onClick={() => {
                              setMenuOpen(false);
                              setMode("speaker");
                            }}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${isActive
                              ? "bg-black text-white font-semibold hover:text-white"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>
                        );
                      }

                      if (item.title === "Sponsor") {
                        if (!sponsorData || sponsorData.length === 0) return null;
                        const path = getPath("sponsor");
                        const isActive =
                          pathName === path && activeTab === "sponsor";
                        return (
                          <Link
                            key={index}
                            to={path}
                            onClick={() => {
                              setMenuOpen(false);
                              setMode("sponsor");
                            }}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${isActive
                              ? "bg-black text-white font-semibold hover:text-white"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>
                        );
                      }

                      if (item.title === "Venue") {
                        const path = getPath("venue");
                        const isActive = pathName === path && activeTab === "venue";
                        return (
                          <Link
                            key={index}
                            to={path}
                            onClick={() => {
                              setMenuOpen(false);
                              setMode("venue");
                            }}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${isActive
                              ? "bg-black text-white font-semibold hover:text-white"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>
                        );
                      }

                      if (item.title === "Tickets") {
                        if (!siteHideData?.ticket || eventEnded) return null;
                        const path = getPath("tickets");
                        return (
                          <Link
                            key={index}
                            to={path}
                            className={`text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4 ${pathName.startsWith(path)
                              ? "bg-black text-white font-semibold"
                              : "text-[#000000] hover:text-[#3A3A3A]"
                              }`}
                          >
                            {item.title}
                          </Link>
                        );
                      }

                      // Handle dropdown menu items in mobile
                      if (item.hasDropdown && item.dropdownItems) {
                        const isExpanded = expandedMobileDropdown === index;
                        const isActive = item.dropdownItems.some((dropdownItem) => {
                          const resolvedPath = dropdownItem.path
                            .replace(":slug", siteSlug || eventData?.slug || "")
                            .replace(":eventId", eventId || "");
                          const pathOnly = resolvedPath.split("?")[0];
                          const currentPathOnly = pathName.split("?")[0];
                          return (
                            currentPathOnly === pathOnly ||
                            pathName === resolvedPath ||
                            pathName.includes(pathOnly)
                          );
                        });

                        return (
                          <div key={index} className="w-full border-b">
                            <button
                              onClick={() =>
                                setExpandedMobileDropdown(isExpanded ? null : index)
                              }
                              className={`w-full text-left text-base transition-colors pl-6 py-4 flex items-center justify-between ${isActive
                                ? "bg-black text-white font-semibold"
                                : "text-[#131517] hover:text-[#3A3A3A]"
                                }`}
                            >
                              {item.title}
                              <ChevronDown
                                className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-180" : ""
                                  }`}
                              />
                            </button>
                            {isExpanded && (
                              <div className="bg-gray-50">
                                {item.dropdownItems.map(
                                  (dropdownItem, dropIndex) => {
                                    const resolvedPath = dropdownItem.path
                                      .replace(
                                        ":slug",
                                        siteSlug || eventData?.slug || "",
                                      )
                                      .replace(":eventId", eventId || "");
                                    const pathOnly = resolvedPath.split("?")[0];
                                    const currentPathOnly = pathName.split("?")[0];
                                    const isDropdownItemActive =
                                      currentPathOnly === pathOnly ||
                                      pathName === resolvedPath ||
                                      pathName.includes(pathOnly);

                                    return (
                                      <Link
                                        key={dropIndex}
                                        to={resolvedPath}
                                        onClick={() => {
                                          setMenuOpen(false);
                                          setExpandedMobileDropdown(null);
                                        }}
                                        className={`block pl-12 py-3 text-sm transition-colors border-b border-gray-200 ${isDropdownItemActive
                                          ? "bg-gray-200 text-black font-semibold"
                                          : "text-gray-700 hover:bg-gray-100"
                                          }`}
                                      >
                                        {dropdownItem.title}
                                      </Link>
                                    );
                                  },
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={index}
                          to={
                            item.path && item.path.startsWith("/")
                              ? item.path
                              : getPath(item.path || "")
                          }
                          onClick={() => setMenuOpen(false)}
                          className="text-base border-b w-full text-[#131517] hover:text-[#3A3A3A] transition-colors pl-6 py-4"
                        >
                          {item.title}
                        </Link>
                      );
                    })}

                  <div className="w-full border-b">
                    <button
                      onClick={() => {
                        setMobileProfileOpen(false);
                        setExpandedMobileDropdown(
                          expandedMobileDropdown === "exhibitors"
                            ? null
                            : "exhibitors",
                        );
                      }}
                      className={`w-full text-left text-base transition-colors pl-6 py-4 flex items-center justify-between pr-4 ${expandedMobileDropdown === "exhibitors" ||
                        pathName === getPath("exhibitors") ||
                        pathName === getPath("/exhibitors/category")
                        ? "bg-black text-white font-semibold"
                        : "text-[#131517] hover:text-[#3A3A3A]"
                        }`}
                    >
                      Exhibitors
                      <ChevronDown className="w-5 h-5" />
                    </button>
                    {expandedMobileDropdown === "exhibitors" && (
                      <div className="bg-gray-50">
                        <Link
                          to={getPath("exhibitors")}
                          onClick={() => {
                            setMenuOpen(false);
                            setExpandedMobileDropdown(null);
                          }}
                          className={`block pl-12 py-3 text-sm border-b border-gray-200 ${pathName === getPath("exhibitors")
                            ? "text-black- font-semibold"
                            : "text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          Exhibitors Listing
                        </Link>
                        <Link
                          to={getPath("exhibitors/category")}
                          onClick={() => {
                            setMenuOpen(false);
                            setExpandedMobileDropdown(null);
                          }}
                          className={`block pl-12 py-3 text-sm border-b border-gray-200 ${pathName === getPath("exhibitors/category")
                            ? "text-black font-semibold"
                            : "text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          Apply To Exhibit
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Permanent Animated PWA Install Card in Mobile Sidebar */}
              <SidebarPwaInstall dark={dark} onActionComplete={() => setMenuOpen(false)} />
            </div>
          </div>
        )}
      </div>

      {cropOpen && (
        <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">
                Adjust Logo
              </div>
              <button
                onClick={cancelCrop}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div
                ref={cropBoxRef}
                className="relative mx-auto bg-gray-100 overflow-hidden"
                style={{
                  width: 360,
                  height: 360 * (280 / 600),
                  touchAction: "none",
                }}
                onWheel={onWheelZoom}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
              >
                <div className="absolute inset-0">
                  {imgElement && (
                    <img
                      src={rawImage?.src}
                      alt=""
                      draggable={false}
                      style={{
                        width: imgSize.w * baseScale,
                        height: imgSize.h * baseScale,
                        maxWidth: "none",
                        maxHeight: "none",
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
                        transformOrigin: "center center",
                        userSelect: "none",
                        willChange: "transform",
                        display: "block",
                      }}
                    />
                  )}
                </div>
                <div className="absolute inset-0 ring-2 ring-black/10 pointer-events-none z-10"></div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-gray-400">
                <div className="w-5 h-5 flex items-center justify-center font-bold text-xl cursor-default select-none">
                  −
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="4"
                  step="0.01"
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00AC00]"
                />
                <div className="w-5 h-5 flex items-center justify-center font-bold text-xl cursor-default select-none">
                  +
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={cancelCrop}
                className="px-6 py-2 border border-gray-400 rounded-full text-black hover:bg-gray-50 font-medium"
              >
                Close
              </button>
              <button
                onClick={confirmCrop}
                className="px-8 py-2 bg-black text-white rounded-full hover:bg-gray-800 font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exhibitor Profile Modals */}
      {isExhibitor &&
        (profileModalMode === "auto" ? (
          <ExhibitorOnboarding
            isOpen={profileSettingPopup}
            onClose={() => {
              setProfileSettingPopup(false);
              setProfileModalMode("auto");
            }}
            siteUser={siteUser}
            exhibitor={siteUser?.exhibitor}
          />
        ) : (
          <ExhibitorProfileSettings
            isOpen={profileSettingPopup}
            onClose={() => {
              setProfileSettingPopup(false);
              setProfileModalMode("auto");
            }}
            siteUser={siteUser}
            exhibitor={siteUser?.exhibitor}
          />
        ))}
    </div>
  );
};
